import * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { DataTableColumnPhone, DataTablePhoneLayout } from "../types";
import styles from "../DataTable/DataTable.module.css";

const classes = (...values: (string | false | undefined)[]) => values.filter(Boolean).join(" ");

export interface TableContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  flush?: boolean;
  fitContent?: boolean;
}

/** The same table surface for a TanStack table or domain-controlled rows. */
export function TableContainer({ flush, fitContent, className, ...props }: TableContainerProps) {
  return <div {...props} className={classes(styles.wrapper, flush && styles.wrapperFlush, fitContent && styles.wrapperFitContent, className)} />;
}

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  phoneLayout?: DataTablePhoneLayout;
  fitContent?: boolean;
  fixedLayout?: boolean;
}

export function Table({ phoneLayout = "scroll", fitContent, fixedLayout, className, ...props }: TableProps) {
  return <table {...props} className={classes(styles.table, phoneLayout === "stack" && styles.stack, fitContent && styles.tableFitContent, fixedLayout && styles.tableFixedLayout, className)} />;
}

export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /** Hide a stacked header when the page already provides its selection control. */
  hideOnPhone?: boolean;
}

export function TableHeader({ hideOnPhone, className, ...props }: TableHeaderProps) {
  return <thead {...props} className={classes(styles.thead, hideOnPhone && styles.phoneHidden, className)} />;
}

export function TableBody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  interactive?: boolean;
  selected?: boolean;
  active?: boolean;
  group?: boolean;
  /** A row activation keeps links, fields and buttons within the row independent. */
  onActivate?: () => void;
  /** Opt in when the page does not already own arrow-key selection. */
  keyboardNavigation?: boolean;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { interactive, selected, active, group, onActivate, keyboardNavigation = false, className, tabIndex, onKeyDown, ...props },
  ref,
) {
  const clickable = interactive ?? (!!props.onClick || !!onActivate);
  const dataProps = props as { "data-selected"?: string; "data-active"?: string };
  return (
    <tr
      {...props}
      ref={ref}
      className={classes(styles.tr, clickable && styles.trClickable, group && styles.groupRow, className)}
      data-table-row={group ? undefined : "true"}
      data-selected={selected ? "true" : dataProps["data-selected"]}
      data-active={active ? "true" : dataProps["data-active"]}
      tabIndex={tabIndex ?? (onActivate ? 0 : undefined)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || event.target !== event.currentTarget || event.metaKey || event.ctrlKey || event.altKey) return;
        if ((event.key === "Enter" || event.key === " ") && onActivate) {
          event.preventDefault();
          onActivate();
        } else if (keyboardNavigation && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
          const rows = [...event.currentTarget.closest("table")!.querySelectorAll<HTMLTableRowElement>('[data-table-row="true"][tabindex]')];
          const next = rows[rows.indexOf(event.currentTarget) + (event.key === "ArrowDown" ? 1 : -1)];
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }
      }}
    />
  );
});

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: false | "asc" | "desc";
  selection?: boolean;
}

export function TableHead({ sortable, sortDirection = false, selection, align, className, children, onKeyDown, ...props }: TableHeadProps) {
  return (
    <th
      scope="col"
      {...props}
      className={classes(styles.th, sortable && styles.sortable, selection && styles.headSelection, align === "right" && styles.numeric, align === "center" && styles.center, className)}
      aria-sort={sortable ? (sortDirection === "asc" ? "ascending" : sortDirection === "desc" ? "descending" : "none") : props["aria-sort"]}
      tabIndex={props.tabIndex ?? (sortable ? 0 : undefined)}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented && sortable && event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          event.currentTarget.click();
        }
      }}
    >
      {sortable ? <span className={styles.thContent}>{children}<TableSortIcon direction={sortDirection} /></span> : children}
    </th>
  );
}

const PHONE_CELL_CLASS: Record<DataTableColumnPhone, string> = {
  title: styles.cellTitle,
  end: styles.cellEnd,
  hide: styles.cellHide,
  selection: styles.cellSelection,
  full: styles.cellFull,
  actions: styles.cellActions,
};

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  phone?: DataTableColumnPhone;
  align?: "left" | "right" | "center";
}

export function TableCell({ phone, align, className, ...props }: TableCellProps) {
  return <td {...props} className={classes(styles.td, phone && PHONE_CELL_CLASS[phone], align === "right" && styles.numeric, align === "center" && styles.center, className)} />;
}

export function TableEmptyState({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={classes(styles.emptyCell, className)} />;
}

export function TableEmptyRow({ colSpan, children, ...props }: React.HTMLAttributes<HTMLTableRowElement> & { colSpan: number }) {
  return <tr {...props} className={classes(styles.emptyRow, props.className)}><td className={styles.emptyCell} colSpan={colSpan}>{children}</td></tr>;
}

function TableSortIcon({ direction }: { direction: false | "asc" | "desc" }) {
  const Icon = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ArrowUpDown;
  return <Icon aria-hidden="true" className={classes(styles.sortIcon, direction !== false && styles.sortIconActive)} />;
}
